import React from "react";
import { DndContext, closestCenter, KeyboardSensor, PointerSensor, useSensor, useSensors, DragEndEvent } from "@dnd-kit/core";
import { arrayMove, SortableContext, sortableKeyboardCoordinates, verticalListSortingStrategy, useSortable } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import OwLabel from "../OwLabel/OwLabel";
import { useIntl } from "react-intl";
import "./OwSortableList.scss";

export interface IOwSortableListItem {
    id: string;
    labelKey: string;
    visible?: boolean;
    // Static items render locked in place (no drag handle / visibility toggle) and are
    // excluded from reordering. Intended for trailing placeholder rows.
    isStatic?: boolean;
    // Draggable but without the visibility toggle (always-visible items)
    hideToggle?: boolean;
}

type Props = {
    items: IOwSortableListItem[];
    onReorder: (ids: string[]) => void;
    // When provided, each row renders an eye toggle calling back with the row's id
    onToggleVisibility?: (id: string) => void;
    renderItem?: (item: IOwSortableListItem) => React.ReactNode;
};

type RowProps = {
    item: IOwSortableListItem;
    onToggleVisibility?: (id: string) => void;
    renderItem?: (item: IOwSortableListItem) => React.ReactNode;
};

const rowContent = (item: IOwSortableListItem, renderItem?: (item: IOwSortableListItem) => React.ReactNode) => {
    return renderItem?.(item) ?? <OwLabel title={item.labelKey} noBottomMargin={true} />;
};

const OwStaticListRow = ({ item, renderItem }: Omit<RowProps, "onToggleVisibility">) => {
    return (
        <div className={"ow-sortable-list-row static-item"}>
            <div className={"row-drag-handle"}>
                <i className={"fas fa-lock"} />
            </div>
            <div className={"row-content"}>{rowContent(item, renderItem)}</div>
        </div>
    );
};

const OwSortableListRow = ({ item, onToggleVisibility, renderItem }: RowProps) => {
    const intl = useIntl();
    const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: item.id });

    const isVisible = item.visible !== false;
    const showToggle = !!onToggleVisibility && !item.hideToggle;

    // Names the icon-only toggle for screen readers: the action it performs ("Hide price" /
    // "Show price"), which also conveys the row's current visibility state. Only resolved when the
    // toggle renders: callers using renderItem may pass a free-text labelKey that is not a message id.
    const toggleLabel = showToggle
        ? `${intl.formatMessage({ id: isVisible ? "hide" : "show" })} ${intl.formatMessage({
              id: item.labelKey,
              defaultMessage: `_${item.labelKey}_`,
          })}`
        : "";

    const style = {
        transform: CSS.Transform.toString(transform),
        transition,
        opacity: isDragging ? 0.5 : isVisible ? 1 : 0.5,
    };

    return (
        <div ref={setNodeRef} style={style} className={`ow-sortable-list-row ${!isVisible ? "hidden-item" : ""}`}>
            <div className={"row-drag-handle"} {...attributes} {...listeners}>
                <i className={"fas fa-grip-vertical"} />
            </div>
            <div className={"row-content"}>{rowContent(item, renderItem)}</div>
            {showToggle && onToggleVisibility ? (
                <div className={"row-actions"}>
                    <button
                        type={"button"}
                        className={`btn btn-sm ${isVisible ? "btn-outline-secondary" : "btn-outline-warning"}`}
                        aria-label={toggleLabel}
                        onClick={() => onToggleVisibility(item.id)}>
                        <i className={isVisible ? "fas fa-eye" : "fas fa-eye-slash"} aria-hidden={true} />
                    </button>
                </div>
            ) : null}
        </div>
    );
};

/**
 * Generic vertical drag-and-drop sortable list with an optional per-row visibility toggle.
 * Purely presentational: reports the new id order / toggled id via callbacks and holds no state.
 */
export const OwSortableList = (props: Props) => {
    const sensors = useSensors(
        useSensor(PointerSensor, {
            activationConstraint: {
                distance: 8,
            },
        }),
        useSensor(KeyboardSensor, {
            coordinateGetter: sortableKeyboardCoordinates,
        })
    );

    const sortableIds = props.items.filter((item) => !item.isStatic).map((item) => item.id);

    const handleDragEnd = (event: DragEndEvent) => {
        const { active, over } = event;

        if (!over || active.id === over.id) {
            return;
        }

        const oldIndex = sortableIds.indexOf(String(active.id));
        const newIndex = sortableIds.indexOf(String(over.id));

        if (oldIndex === -1 || newIndex === -1) {
            return;
        }

        props.onReorder(arrayMove(sortableIds, oldIndex, newIndex));
    };

    return (
        <div className={"OwSortableList"}>
            <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
                <SortableContext items={sortableIds} strategy={verticalListSortingStrategy}>
                    {props.items.map((item) =>
                        item.isStatic ? (
                            <OwStaticListRow key={item.id} item={item} renderItem={props.renderItem} />
                        ) : (
                            <OwSortableListRow
                                key={item.id}
                                item={item}
                                onToggleVisibility={props.onToggleVisibility}
                                renderItem={props.renderItem}
                            />
                        )
                    )}
                </SortableContext>
            </DndContext>
        </div>
    );
};
